/**
 * The aquatics office: the hours, the guard board, the swim school, and
 * the log of every hour the pool was shut.
 *
 * Four things are drawn rather than explained:
 *
 *   HOURS ARE THE THING A CLUB PLANS. Everything else in the module is
 *   built against them — the sheet, the guard requirement, and whether
 *   there is a pool at all on a given date. So they get the first tab
 *   and they are shown as a WEEK, with the overlaps visible: lap swim
 *   inside open swim is the ordinary summer morning and a screen that
 *   hides it is a screen that has misunderstood the pool.
 *
 *   THE GUARD BOARD LEADS WITH WHAT IS WRONG. An hour with nobody
 *   watching sits at the top, in red, above the rota — because the
 *   board is opened at eight on a Saturday when somebody has called in
 *   sick, and a rota you have to read to find the hole is a rota nobody
 *   reads.
 *
 *   THE CHASE LIST IS SORTED BY HOW SOON. Every club asks "who lapses
 *   before Labor Day" in April, and none of them has a screen for it.
 *   Expired first, then soonest — which is the order somebody works
 *   down a telephone list.
 *
 *   THE WAITLIST IS SHOWN, IN ORDER. A full class with two children
 *   behind it is the ordinary state of a learn-to-swim program, and the
 *   order people asked in is the only fair basis for offering the place
 *   that opens on Thursday. Hiding it makes the director keep the real
 *   list somewhere else.
 */

import { useState, type ReactNode } from 'react';
import { api } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { useSession } from '../lib/session.tsx';
import { Card, Empty, ErrorBanner, Loading, Status } from '../components/ui.tsx';
import { date as fmtDate } from '../lib/format.ts';

/* ------------------------------------------------------------------ */
/* Shapes                                                              */
/* ------------------------------------------------------------------ */

interface Pool {
  id: string;
  code: string;
  name: string;
  kind: string;
  bather_load: number | null;
  lane_count: number;
  area_count: number;
  reservable_lane_count: number;
  is_active: boolean;
  is_open: boolean | null;
  closed_because: string | null;
  hours_lost_this_year: string | null;
  closures_this_year: string | null;
}

interface Season {
  id: string;
  name: string;
  starts_on: string;
  ends_on: string;
  lane_minutes: number;
  open_days_ahead: number;
  is_current: boolean;
  note: string | null;
}

interface Hours {
  id: string;
  season_id: string;
  facility_id: string;
  facility_name: string;
  weekday: number | null;
  day_type: string;
  opens_at: string;
  closes_at: string;
  purpose: string;
  guards_required: number;
  note: string | null;
}

interface Coverage {
  session_id: string;
  facility_name: string;
  swim_date: string;
  starts_at: string;
  ends_at: string;
  purpose: string;
  guards_required: number;
  guards_covering: number;
  guards_partial: number;
  guards_short: number;
  coverage: string;
}

interface BoardRow {
  assignment_id: string;
  guard_date: string;
  starts_at: string;
  ends_at: string;
  status: string;
  facility_name: string;
  post_name: string;
  post_kind: string;
  area_name: string | null;
  guard_name: string;
  certified: boolean;
}

interface Certification {
  certification_id: string;
  guard_name: string;
  job_title: string;
  type_code: string;
  type_name: string;
  agency: string | null;
  qualifies_to_guard: boolean;
  qualifies_to_teach: boolean;
  certificate_number: string | null;
  issued_on: string;
  expires_on: string;
  days_remaining: number;
  standing: string;
  still_employed: boolean;
}

interface Program {
  program_id: string;
  name: string;
  status: string;
  level_code: string;
  level_name: string;
  facility_name: string;
  area_name: string | null;
  instructor_name: string | null;
  starts_on: string;
  ends_on: string;
  weekdays: number[];
  starts_at: string;
  minutes: number;
  capacity: number;
  enrolled: string;
  waitlisted: string;
  places_left: string;
  meetings: string;
  meetings_cancelled: string;
}

interface Enrollment {
  enrollment_id: string;
  status: string;
  waitlist_position: number | null;
  swimmer_name: string | null;
  swimmer_age: number | null;
  membership_number: string | null;
  ar_transaction_id: string | null;
  enrolled_at: string;
}

interface Closure {
  closure_id: string;
  facility_name: string;
  area_name: string | null;
  reason: string;
  note: string | null;
  closed_from: string;
  closed_until: string | null;
  reopened_at: string | null;
  still_closed: boolean;
  hours: string;
}

/* ------------------------------------------------------------------ */
/* Small shared bits                                                   */
/* ------------------------------------------------------------------ */

const hhmm = (t: string | null) => (t ? t.slice(0, 5) : '');
const today = () => new Date().toISOString().slice(0, 10);

const DAYS = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const PURPOSE_LABEL: Record<string, string> = {
  open_swim: 'Open swim',
  lap: 'Lap swim',
  adult: 'Adult swim',
  team: 'Swim team',
  lesson: 'Swim lessons',
  private: 'Private hire',
};

const REASON_LABEL: Record<string, string> = {
  chemical: 'Chemical',
  contamination: 'Contamination',
  weather: 'Weather',
  lightning: 'Lightning',
  staffing: 'No guard',
  maintenance: 'Maintenance',
  other: 'Other',
};

function Actions({ children }: { children: ReactNode }) {
  return <div className="actions">{children}</div>;
}

const TABS = [
  { id: 'hours', label: 'Hours & Seasons' },
  { id: 'guards', label: 'Lifeguards' },
  { id: 'school', label: 'Swim School' },
  { id: 'closures', label: 'Closures' },
] as const;

type Tab = (typeof TABS)[number]['id'];

/* ------------------------------------------------------------------ */
/* The page                                                            */
/* ------------------------------------------------------------------ */

export function PoolAdminPage() {
  const { atLeast } = useSession();
  const canEdit = atLeast('manager');
  const [tab, setTab] = useState<Tab>('hours');

  return (
    <>
      <div className="row" style={{ marginBottom: 16 }}>
        <div>
          <h1>Aquatics</h1>
          <div className="muted small">
            {canEdit
              ? 'The hours, the guards, the swim school, and every hour the pool was shut'
              : 'Read only — a manager changes these'}
          </div>
        </div>
      </div>

      <div className="actions" style={{ marginBottom: 14 }}>
        {TABS.map((t) => (
          <button
            key={t.id}
            className={tab === t.id ? 'gold small' : 'secondary small'}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'hours' && <HoursTab canEdit={canEdit} />}
      {tab === 'guards' && <GuardsTab canEdit={canEdit} />}
      {tab === 'school' && <SchoolTab canEdit={canEdit} />}
      {tab === 'closures' && <ClosuresTab canEdit={canEdit} />}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Hours                                                               */
/* ------------------------------------------------------------------ */

function HoursTab({ canEdit }: { canEdit: boolean }) {
  const pools = useApi<{ pools: Pool[] }>('/v1/pool/pools?include_inactive=true');
  const seasons = useApi<{ seasons: Season[]; holidays: { id: string; holiday_on: string; name: string }[] }>(
    '/v1/pool/seasons',
  );
  const hours = useApi<{ hours: Hours[] }>('/v1/pool/hours');
  const [adding, setAdding] = useState(false);

  const current = seasons.data?.seasons.find((s) => s.is_current) ?? seasons.data?.seasons[0] ?? null;

  const remove = useMutation(async (id: string) => {
    await api(`/v1/pool/hours/${id}`, { method: 'DELETE' });
    hours.reload();
  });

  if (pools.loading || seasons.loading || hours.loading) return <Loading rows={6} />;

  const rows = (hours.data?.hours ?? []).filter((h) => !current || h.season_id === current.id);

  return (
    <>
      <ErrorBanner error={pools.error ?? seasons.error ?? hours.error ?? remove.error} />

      <Card title="The Water">
        {(pools.data?.pools ?? []).length === 0 ? (
          <Empty>No pools yet. A pool arrives with its lanes in one go.</Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th style={{ width: 80 }}>Code</th>
                <th>Pool</th>
                <th style={{ width: 110 }}>Kind</th>
                <th style={{ width: 120 }}>Posted load</th>
                <th style={{ width: 110 }}>Lanes</th>
                <th style={{ width: 160 }}>Right now</th>
              </tr>
            </thead>
            <tbody>
              {(pools.data?.pools ?? []).map((p) => (
                <tr key={p.id} className={p.is_active ? undefined : 'muted'}>
                  <td className="mono">{p.code}</td>
                  <td>{p.name}</td>
                  <td>{p.kind}</td>
                  <td className="num">{p.bather_load ?? '—'}</td>
                  <td className="num">{p.reservable_lane_count}</td>
                  <td>
                    {p.is_open === false ? (
                      <Status tone="critical">Closed</Status>
                    ) : (
                      <Status tone="good">Open</Status>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card title="Seasons">
        <div className="muted small" style={{ marginBottom: 10 }}>
          Outside a season there is no pool at all, and no sheet to open. That is
          not the same as the pool being closed, which is a closure and carries a
          reason.
        </div>
        {(seasons.data?.seasons ?? []).length === 0 ? (
          <Empty>No season yet. Nothing can be opened until there is one.</Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Season</th>
                <th style={{ width: 220 }}>Dates</th>
                <th style={{ width: 120 }}>Lane block</th>
                <th style={{ width: 160 }}>Sheet opens</th>
                <th style={{ width: 100 }} />
              </tr>
            </thead>
            <tbody>
              {(seasons.data?.seasons ?? []).map((s) => (
                <tr key={s.id}>
                  <td>
                    {s.name}
                    {s.note && <div className="hint">{s.note}</div>}
                  </td>
                  <td>
                    {fmtDate(s.starts_on)} – {fmtDate(s.ends_on)}
                  </td>
                  <td className="num">{s.lane_minutes} min</td>
                  <td className="num">{s.open_days_ahead} days ahead</td>
                  <td>{s.is_current && <Status tone="good">Now</Status>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card
        title="The Week"
        actions={
          canEdit && !adding ? (
            <button className="gold small" onClick={() => setAdding(true)}>
              Add a block
            </button>
          ) : null
        }
      >
        <div className="muted small" style={{ marginBottom: 10 }}>
          Several blocks run at once and that is not a mistake — lap swim inside
          open swim is the ordinary summer morning. Two of the SAME thing
          overlapping is a block typed twice, and is refused.
        </div>

        {adding && current && (
          <AddHours
            season={current}
            pools={pools.data?.pools ?? []}
            onDone={() => {
              setAdding(false);
              hours.reload();
            }}
            onCancel={() => setAdding(false)}
          />
        )}

        {rows.length === 0 ? (
          <Empty>No hours planned for this season.</Empty>
        ) : (
          <div className="grid-2">
            {[1, 2, 3, 4, 5, 6, 7].map((w) => {
              const day = rows.filter((h) => h.weekday === w);
              return (
                <div key={w} className="week__day">
                  <div className="week__head">{DAYS[w]}</div>
                  {day.length === 0 ? (
                    <div className="week__none">Shut</div>
                  ) : (
                    day
                      .slice()
                      .sort((a, b) => a.opens_at.localeCompare(b.opens_at))
                      .map((h) => (
                        <div key={h.id} className="shift">
                          <span className="shift__time mono">
                            {hhmm(h.opens_at)}–{hhmm(h.closes_at)}
                          </span>{' '}
                          <span className="shift__title">
                            {PURPOSE_LABEL[h.purpose] ?? h.purpose}
                          </span>{' '}
                          <span className="shift__who muted small">
                            {h.facility_name} · {h.guards_required} guard
                            {h.guards_required === 1 ? '' : 's'}
                          </span>
                          {canEdit && (
                            <button
                              className="secondary small"
                              disabled={remove.busy}
                              onClick={() => remove.run(h.id)}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      ))
                  )}
                </div>
              );
            })}
          </div>
        )}

        {rows.some((h) => h.day_type === 'holiday') && (
          <div className="notice" style={{ marginTop: 14 }}>
            <strong>Holiday hours are written.</strong> On a day the club has
            marked a holiday, these replace the ordinary day entirely. A holiday
            with no hours of its own runs the weekday it lands on, which is
            deliberate — a club that marks the Fourth and forgets the hours
            should get its Saturday, not a closed pool.
          </div>
        )}
      </Card>
    </>
  );
}

function AddHours({
  season,
  pools,
  onDone,
  onCancel,
}: {
  season: Season;
  pools: Pool[];
  onDone: () => void;
  onCancel: () => void;
}) {
  const [f, setF] = useState({
    facility_id: pools[0]?.id ?? '',
    weekdays: [1, 2, 3, 4, 5] as number[],
    opens_at: '11:00',
    closes_at: '20:00',
    purpose: 'open_swim',
    guards_required: 2,
  });

  const add = useMutation(async () => {
    await api('/v1/pool/hours', {
      method: 'POST',
      body: { season_id: season.id, ...f },
    });
    onDone();
  });

  const toggle = (w: number) =>
    setF({
      ...f,
      weekdays: f.weekdays.includes(w)
        ? f.weekdays.filter((x) => x !== w)
        : [...f.weekdays, w].sort(),
    });

  return (
    <div style={{ marginBottom: 16 }}>
      <ErrorBanner error={add.error} />
      <div className="muted small" style={{ marginBottom: 8 }}>
        A club says "Monday to Friday, eleven till eight" as one fact. Typing it
        five times is how one of the five ends up different from the other four.
      </div>

      <div className="grid-2">
        <label className="field">
          <span>Pool</span>
          <select
            value={f.facility_id}
            onChange={(e) => setF({ ...f, facility_id: e.target.value })}
          >
            {pools.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span>What for</span>
          <select value={f.purpose} onChange={(e) => setF({ ...f, purpose: e.target.value })}>
            {Object.entries(PURPOSE_LABEL).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span>Opens</span>
          <input
            type="time"
            value={f.opens_at}
            onChange={(e) => setF({ ...f, opens_at: e.target.value })}
          />
        </label>

        <label className="field">
          <span>Closes</span>
          <input
            type="time"
            value={f.closes_at}
            onChange={(e) => setF({ ...f, closes_at: e.target.value })}
          />
        </label>

        <label className="field">
          <span>Guards wanted</span>
          <input
            type="number"
            min={0}
            max={12}
            value={f.guards_required}
            onChange={(e) => setF({ ...f, guards_required: Number(e.target.value) })}
          />
        </label>
      </div>

      <div className="field">
        <span>Which days</span>
        <Actions>
          {[1, 2, 3, 4, 5, 6, 7].map((w) => (
            <button
              key={w}
              className={f.weekdays.includes(w) ? 'gold small' : 'secondary small'}
              onClick={() => toggle(w)}
            >
              {DAYS[w]}
            </button>
          ))}
        </Actions>
      </div>

      <Actions>
        <button
          className="gold"
          disabled={add.busy || f.weekdays.length === 0 || !f.facility_id}
          onClick={() => add.run()}
        >
          {add.busy ? 'Adding…' : 'Add it'}
        </button>
        <button className="secondary" onClick={onCancel}>
          Cancel
        </button>
      </Actions>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Guards                                                              */
/* ------------------------------------------------------------------ */

function GuardsTab({ canEdit }: { canEdit: boolean }) {
  const [date, setDate] = useState(today());
  const board = useApi<{ assignments: BoardRow[]; coverage: Coverage[] }>(
    `/v1/pool/guards/board?date=${date}`,
    [date],
  );
  const certs = useApi<{ certifications: Certification[] }>(
    canEdit ? '/v1/pool/guards/certifications' : null,
  );

  const holes = (board.data?.coverage ?? []).filter(
    (c) => c.coverage === 'uncovered' || c.coverage === 'partly_covered',
  );

  return (
    <>
      <ErrorBanner error={board.error ?? certs.error} />

      {/* What is wrong, above the schedule. The board is opened at
          eight on a Saturday when somebody has called in sick, and a
          schedule you have to read to find the hole is a schedule
          nobody reads. */}
      {holes.length > 0 && (
        <div className={holes.some((h) => h.coverage === 'uncovered') ? 'error-banner' : 'notice'}>
          <strong>
            {holes.filter((h) => h.coverage === 'uncovered').length > 0
              ? 'Nobody is watching the water for part of this day.'
              : 'The water is short of guards for part of this day.'}
          </strong>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {holes.map((h) => (
              <li key={h.session_id}>
                {hhmm(h.starts_at)}–{hhmm(h.ends_at)} {PURPOSE_LABEL[h.purpose] ?? h.purpose} at{' '}
                {h.facility_name} —{' '}
                {h.coverage === 'uncovered'
                  ? 'nobody on a stand'
                  : `${h.guards_short} short of ${h.guards_required}`}
              </li>
            ))}
          </ul>
        </div>
      )}

      <Card
        title="The Board"
        actions={
          <Actions>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </Actions>
        }
      >
        {board.loading ? (
          <Loading rows={4} />
        ) : (board.data?.assignments ?? []).length === 0 ? (
          <Empty>
            Nobody is on the board for this day. An unguarded hour is shown here
            rather than refused — the sheet is written in March and the guards
            are scheduled in May, and the software cannot refuse to record what
            actually happened.
          </Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th style={{ width: 140 }}>Hours</th>
                <th>Post</th>
                <th>Guard</th>
                <th style={{ width: 130 }}>Card</th>
                <th style={{ width: 110 }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {(board.data?.assignments ?? []).map((a) => (
                <tr key={a.assignment_id} className={a.status === 'cancelled' ? 'muted' : undefined}>
                  <td className="nowrap mono">
                    {hhmm(a.starts_at)}–{hhmm(a.ends_at)}
                  </td>
                  <td>
                    {a.post_name}
                    <div className="hint">
                      {/* The wading pool is one pool with one area, both
                          called the wading pool. Printing it twice reads
                          as a bug in the data rather than as a place. */}
                      {a.facility_name}
                      {a.area_name && a.area_name !== a.facility_name
                        ? ` · ${a.area_name}`
                        : ''}
                    </div>
                  </td>
                  <td>{a.guard_name}</td>
                  <td>
                    {a.certified ? (
                      <Status tone="good">Current</Status>
                    ) : (
                      <Status tone="critical">Lapsed</Status>
                    )}
                  </td>
                  <td>{a.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card title="Who Lapses When">
        <div className="muted small" style={{ marginBottom: 10 }}>
          The April question. Expired first, then soonest — the order somebody
          works down a telephone list.
        </div>
        {!canEdit ? (
          <Empty>A manager sees the certificates.</Empty>
        ) : certs.loading ? (
          <Loading rows={4} />
        ) : (certs.data?.certifications ?? []).length === 0 ? (
          <Empty>No certificates recorded yet.</Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Who</th>
                <th>Certificate</th>
                <th style={{ width: 120 }}>Number</th>
                <th style={{ width: 130 }}>Expires</th>
                <th style={{ width: 150 }}>Standing</th>
              </tr>
            </thead>
            <tbody>
              {(certs.data?.certifications ?? []).map((c) => (
                <tr key={c.certification_id}>
                  <td>
                    {c.guard_name}
                    <div className="hint">{c.job_title}</div>
                  </td>
                  <td>
                    {c.type_name}
                    {c.agency && <div className="hint">{c.agency}</div>}
                  </td>
                  <td className="mono">{c.certificate_number ?? '—'}</td>
                  <td>{fmtDate(c.expires_on)}</td>
                  <td>
                    {c.standing === 'expired' ? (
                      <Status tone="critical">Expired</Status>
                    ) : c.standing === 'expiring' ? (
                      <Status tone="serious">{c.days_remaining} days</Status>
                    ) : (
                      <Status tone="good">{c.days_remaining} days</Status>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* The swim school                                                     */
/* ------------------------------------------------------------------ */

function SchoolTab({ canEdit }: { canEdit: boolean }) {
  const programs = useApi<{ programs: Program[] }>('/v1/pool/programs');
  const [open, setOpen] = useState<string | null>(null);

  if (programs.loading) return <Loading rows={5} />;

  return (
    <>
      <ErrorBanner error={programs.error} />
      <Card title="Classes">
        <div className="muted small" style={{ marginBottom: 10 }}>
          A place in a class is one row and the whole four weeks is what a parent
          buys. Storing it as eight bookings is what makes a class full on Tuesday
          and empty on Thursday.
        </div>

        {(programs.data?.programs ?? []).length === 0 ? (
          <Empty>No classes yet.</Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Class</th>
                <th style={{ width: 180 }}>Runs</th>
                <th style={{ width: 130 }}>When</th>
                <th style={{ width: 100 }}>Places</th>
                <th style={{ width: 110 }}>Waitlist</th>
                <th style={{ width: 110 }} />
              </tr>
            </thead>
            <tbody>
              {(programs.data?.programs ?? []).map((p) => (
                <tr key={p.program_id}>
                  <td>
                    {p.name}
                    <div className="hint">
                      {p.level_name}
                      {p.instructor_name ? ` · ${p.instructor_name}` : ''}
                      {p.area_name ? ` · ${p.area_name}` : ''}
                    </div>
                  </td>
                  <td>
                    {fmtDate(p.starts_on)} – {fmtDate(p.ends_on)}
                    <div className="hint">
                      {(p.weekdays ?? []).map((w) => DAYS[w]).join(', ')}
                    </div>
                  </td>
                  <td className="mono">
                    {hhmm(p.starts_at)} · {p.minutes}m
                  </td>
                  <td className="num">
                    {Number(p.places_left) === 0 ? (
                      <Status tone="serious">Full</Status>
                    ) : (
                      `${p.places_left} of ${p.capacity}`
                    )}
                  </td>
                  <td className="num">
                    {Number(p.waitlisted) > 0 ? (
                      <Status tone="warning">{p.waitlisted} waiting</Status>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="num">
                    <button
                      className="secondary small"
                      onClick={() => setOpen(open === p.program_id ? null : p.program_id)}
                    >
                      {open === p.program_id ? 'Hide' : 'Roster'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {open && <Roster programId={open} canEdit={canEdit} />}
    </>
  );
}

function Roster({ programId, canEdit }: { programId: string; canEdit: boolean }) {
  const roster = useApi<{
    enrollments: Enrollment[];
    meetings: { id: string; meets_on: string; starts_at: string; status: string; cancelled_reason: string | null }[];
  }>(`/v1/pool/programs/${programId}/roster`, [programId]);

  if (roster.loading) return <Loading rows={4} />;

  const enrolled = (roster.data?.enrollments ?? []).filter((e) => e.status === 'enrolled');
  const queue = (roster.data?.enrollments ?? []).filter((e) => e.status === 'waitlisted');

  return (
    <>
      <ErrorBanner error={roster.error} />
      <Card title="In the Class">
        {enrolled.length === 0 ? (
          <Empty>Nobody enrolled yet.</Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Swimmer</th>
                <th style={{ width: 90 }}>Age</th>
                <th style={{ width: 140 }}>Membership</th>
                <th style={{ width: 120 }}>Billed</th>
              </tr>
            </thead>
            <tbody>
              {enrolled.map((e) => (
                <tr key={e.enrollment_id}>
                  <td>{e.swimmer_name}</td>
                  <td className="num">{e.swimmer_age ?? '—'}</td>
                  <td className="mono">{e.membership_number ?? '—'}</td>
                  <td>
                    {e.ar_transaction_id ? (
                      <Status tone="good">Yes</Status>
                    ) : (
                      <Status tone="neutral">No charge</Status>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {queue.length > 0 && (
        <Card title="Waiting for a Place">
          <div className="muted small" style={{ marginBottom: 10 }}>
            In the order they asked, which is the only fair basis for offering the
            place that opens up on Thursday. Nobody here has been charged.
          </div>
          <table className="data">
            <thead>
              <tr>
                <th style={{ width: 70 }}>#</th>
                <th>Swimmer</th>
                <th style={{ width: 90 }}>Age</th>
                <th style={{ width: 140 }}>Membership</th>
              </tr>
            </thead>
            <tbody>
              {queue.map((e) => (
                <tr key={e.enrollment_id}>
                  <td className="num mono">{e.waitlist_position}</td>
                  <td>{e.swimmer_name}</td>
                  <td className="num">{e.swimmer_age ?? '—'}</td>
                  <td className="mono">{e.membership_number ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {(roster.data?.meetings ?? []).some((m) => m.status === 'cancelled') && (
        <Card title="Mornings That Did Not Happen">
          <div className="muted small" style={{ marginBottom: 10 }}>
            Cancelling one does not touch the money. What it does is make the
            club's record of what was actually delivered true, which is the only
            ground a refund conversation can be had on.
          </div>
          <table className="data">
            <thead>
              <tr>
                <th style={{ width: 160 }}>Date</th>
                <th>Why</th>
              </tr>
            </thead>
            <tbody>
              {(roster.data?.meetings ?? [])
                .filter((m) => m.status === 'cancelled')
                .map((m) => (
                  <tr key={m.id}>
                    <td>{fmtDate(m.meets_on)}</td>
                    <td>{m.cancelled_reason}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </Card>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Closures                                                            */
/* ------------------------------------------------------------------ */

function ClosuresTab({ canEdit }: { canEdit: boolean }) {
  const closures = useApi<{
    closures: Closure[];
    status: {
      facility_id: string;
      facility_name: string;
      is_open: boolean;
      closed_because: string | null;
      closures_this_year: string;
      hours_lost_this_year: string | null;
    }[];
  }>('/v1/pool/closures');

  const reopen = useMutation(async (facilityId: string) => {
    await api('/v1/pool/closures/reopen', {
      method: 'POST',
      body: { facility_id: facilityId },
    });
    closures.reload();
  });

  if (closures.loading) return <Loading rows={5} />;

  return (
    <>
      <ErrorBanner error={closures.error ?? reopen.error} />

      <div className="tiles">
        {(closures.data?.status ?? []).map((s) => (
          <div key={s.facility_id} className="tile">
            <div className="tile__label">{s.facility_name}</div>
            <div className="tile__value">
              {s.is_open ? (
                <Status tone="good">Open</Status>
              ) : (
                <Status tone="critical">Closed</Status>
              )}
            </div>
            <div className="tile__note">
              {s.closed_because ?? `${s.hours_lost_this_year ?? 0} hours lost this year`}
              {!s.is_open && canEdit && (
                <div style={{ marginTop: 6 }}>
                  <button
                    className="secondary small"
                    disabled={reopen.busy}
                    onClick={() => reopen.run(s.facility_id)}
                  >
                    Open it again
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <Card title="Every Hour the Pool Was Shut">
        <div className="muted small" style={{ marginBottom: 10 }}>
          A season of these answers "how many hours did we lose and to what" —
          which no club can currently answer and every board asks. Lightning is
          its own reason and not weather, because it has a clock.
        </div>
        {(closures.data?.closures ?? []).length === 0 ? (
          <Empty>The pool has not closed this year.</Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th style={{ width: 150 }}>Reason</th>
                <th>What happened</th>
                <th style={{ width: 160 }}>From</th>
                <th style={{ width: 100 }}>Hours</th>
                <th style={{ width: 120 }}>State</th>
              </tr>
            </thead>
            <tbody>
              {(closures.data?.closures ?? []).map((c) => (
                <tr key={c.closure_id}>
                  <td>{REASON_LABEL[c.reason] ?? c.reason}</td>
                  <td>
                    {c.note ?? '—'}
                    <div className="hint">
                      {c.facility_name}
                      {c.area_name && c.area_name !== c.facility_name
                        ? ` · ${c.area_name}`
                        : ''}
                    </div>
                  </td>
                  <td className="nowrap">{fmtDate(c.closed_from)}</td>
                  <td className="num">{c.hours}</td>
                  <td>
                    {c.still_closed ? (
                      <Status tone="critical">Still shut</Status>
                    ) : (
                      <Status tone="neutral">Reopened</Status>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}
